import { Link } from "react-router-dom";
import type { FactResponse } from "../api/client.js";
import { filingHref } from "../lib/citations.js";
import { FACT_LABEL, factDisplay } from "../lib/format.js";
import type { FactKind } from "@mosaic/shared";

/**
 * The terms grid. Every cell that has a value also shows where it came from, because a
 * number that cannot be traced back to a filing is not one to reason from.
 *
 * A cell with no fact reads "unknown", never 0 or a blank. Absence is information.
 *
 * **Every cell says how it was got.** A ratio matched by a rule over the front of a filing
 * and one read out of the whole document are not the same claim, and a grid rendering them
 * identically would let a situation look read when nothing had read it.
 */

/*
  Which kinds the grid shows and in what order, a different question from what a kind is
  called, which is `FACT_LABEL` and shared with the filing delta. `Retained` is the one
  place the grid overrides: the cell is narrow and the delta's rows are not.
*/
const CELLS: Array<{ kind: FactKind; label?: string; highlight?: boolean }> = [
  { kind: "distribution_ratio", highlight: true },
  { kind: "record_date" },
  { kind: "distribution_date" },
  { kind: "tax_treatment" },
  { kind: "spinco_debt", highlight: true },
  { kind: "parent_retained_stake_pct", label: "Retained" },
  { kind: "spinco_ticker" },
  { kind: "spinco_exchange" },
  { kind: "when_issued_start" },
  { kind: "tsa_term_months" },
];

/**
 * The situation's distribution terms as a grid of cells, each with its source, and
 * `unknown` for a key term no fact supplies.
 *
 * @param facts The situation's current facts; kinds the grid does not show are ignored.
 */
export function TermsGrid({ facts }: { facts: FactResponse[] }) {
  const byKind = new Map(facts.map((f) => [f.kind, f]));
  // Only cells with a fact, plus those always worth showing as unknown: a missing ratio or
  // record date is itself the news.
  const always = new Set(["distribution_ratio", "record_date", "distribution_date", "spinco_debt"]);
  const cells = CELLS.filter((c) => byKind.has(c.kind) || always.has(c.kind));

  return (
    <div className="terms">
      {cells.map((cell) => {
        const fact = byKind.get(cell.kind);
        const value = fact ? factDisplay(cell.kind, fact.value) : "unknown";
        return (
          <div key={cell.kind}>
            <div className="k">{cell.label ?? FACT_LABEL[cell.kind]}</div>
            <div className={`v${!fact ? " unknown" : cell.highlight ? " hi" : ""}`}>{value}</div>
            {fact && <Provenance fact={fact} />}
          </div>
        );
      })}
    </div>
  );
}

/**
 * Where a fact's value came from: `yours` and the note for an owner-entered value,
 * otherwise the method and a link to the cited passage.
 *
 * @internal
 */
function Provenance({ fact }: { fact: FactResponse }) {
  if (fact.method === "user") {
    return (
      <div className="src">
        <span className="user">yours</span>
        {fact.note ? ` · ${fact.note}` : ""}
      </div>
    );
  }
  if (!fact.document) return <div className="src">{fact.method}</div>;

  const { accession, formType, id } = fact.document;
  return (
    <div className="src">
      {/*
        The filing's own hedge, beside the value rather than folded into it: "1:4" alone
        cannot say the document called it an assumption. It is a different word from the
        method after it, which says who read the value; this says what the document
        committed to. The method words are the stored `method` values, unchanged, as
        `FlagList` renders them.
      */}
      {fact.tentative && (
        <>
          <span className="by assumed" title="The filing calls this assumed, not settled — it does not advance the stage.">
            assumed
          </span>
          {" · "}
        </>
      )}
      <span className={`by ${fact.method}`}>{fact.method}</span>{" · "}
      {/*
        `doc` names the document the offsets belong to; without it the reader picks the
        largest, and on a filing where those differ highlights an unrelated passage as
        the source.
      */}
      <Link
        to={filingHref(accession, { documentId: id, charStart: fact.charStart, charEnd: fact.charEnd })}
        title={fact.quote ?? undefined}
      >
        {formType ?? accession}
      </Link>
      {fact.charStart !== null ? ` @${fact.charStart.toLocaleString()}` : ""}
    </div>
  );
}
